import { useCallback, useState } from 'react';
import TipsDialog from '../components/TipsDialog/TipsDialog';

type TipsPromise = {
  submit: (tipAmount: number) => void;
  cancel: () => void;
};

export const useTipDialog = () => {
  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [tipsPromise, setTipsPromise] = useState<TipsPromise | undefined>(undefined);

  const promptForTips = useCallback((promptTips: boolean): Promise<number> => {
    if (!promptTips) {
      return Promise.resolve(0);
    }

    return new Promise((resolve) => {
      if (!promptTips) {
        resolve(0);
      }

      const cancel = () => {
        setTimeout(() => setShowTipsDialog(false), 500);

        resolve(0);
      };

      const submit = (tipAmount: number) => {
        setTimeout(() => setShowTipsDialog(false), 5000);

        resolve(tipAmount);
      };

      setTipsPromise({ submit, cancel });
      setShowTipsDialog(true);
    });
  }, []);

  return {
    promptForTips: promptForTips,
    showTipsDialog: showTipsDialog,
    tipsPromise: tipsPromise,
  };
};

export const useTipDialogRemake = () => {
  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [tipState, setTipState] = useState<{
    totalPrice: number;
    currency: string;
    resolve: (tip: number) => void;
  } | null>(null);

  const promptForTips = useCallback((promptTips: boolean, totalPrice: number, currency: string): Promise<number> => {
    if (!promptTips) return Promise.resolve(0);

    return new Promise((resolve) => {
      setTipState({ totalPrice, currency, resolve });
      setShowTipsDialog(true);
    });
  }, []);

  const cancelTip = useCallback(() => {
    tipState?.resolve(0);
    setShowTipsDialog(false);
    setTipState(null);
  }, [tipState]);

  const confirmTip = useCallback(
    (tipAmount: number) => {
      tipState?.resolve(tipAmount);
      setShowTipsDialog(false);
      setTipState(null);
    },
    [tipState]
  );

  const TipsDialogUI = tipState ? (
    <TipsDialog
      cancelTip={cancelTip}
      confirmTip={confirmTip}
      totalPrice={tipState.totalPrice}
      currency={tipState.currency}
    />
  ) : null;

  return { promptForTips, TipsDialogUI };
};
